import { useState, useMemo } from "react";
import { resumoProjetos } from "@/mocks/projetos";
import { useProjectsList } from "@/hooks";
import ProjectCard from "./components/ProjectCard";
import ProjectTable from "./components/ProjectTable";
import ProjectFilters from "./components/ProjectFilters";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import EmptyState from "@/components/shared/EmptyState";

export default function Projetos() {
  const [viewMode, setViewMode] = useState<"cards" | "table">("cards");
  const [filters, setFilters] = useState<Record<string, string>>({});
  const { items: projetos } = useProjectsList();

  const filtered = useMemo(() => {
    return projetos.filter((p) => {
      if (filters.search && !p.nome.toLowerCase().includes(filters.search)) return false;
      if (filters.municipio && !p.municipio.toLowerCase().includes(filters.municipio)) return false;
      if (filters.uf && p.uf !== filters.uf) return false;
      if (filters.tipo && p.tipo !== filters.tipo) return false;
      if (filters.status && p.status !== filters.status) return false;
      if (filters.responsavel && p.responsavel !== filters.responsavel) return false;
      return true;
    });
  }, [filters, projetos]);

  const projetosAtrasados = projetos.filter((p) => {
    if (p.status === "concluido" || p.status === "homologado" || p.status === "arquivado") return false;
    const hoje = new Date("2026-07-01");
    const previsao = new Date(p.dataPrevisao);
    return previsao < hoje && p.progresso < 100;
  });

  const projetosCriticos = projetos.filter((p) => p.riscoCritico > 10);
  const projetosBaixaEvolucao = projetos.filter((p) => p.status === "em campo" && p.progresso < 40);
  const projetosRelatorioPendente = projetos.filter((p) => p.status === "em revisão");
  const projetosDadosIncompletos = projetos.filter((p) => p.arvoresRevisao > 50);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Projetos de Inventário"
        subtitle="Gerencie campanhas de implantação do CTA, inventários urbanos, áreas verdes, UCs e monitoramentos ambientais."
      >
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-upload-cloud-line"></i>
          <span className="hidden sm:inline">Importar Base</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer">
          <i className="ri-download-line"></i>
          <span className="hidden sm:inline">Exportar Lista</span>
        </button>
        <button className="btn-primary text-xs px-3 py-2 cursor-pointer">
          <i className="ri-add-line"></i>
          <span className="hidden sm:inline">Novo Projeto</span>
        </button>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <KpiCard label="Projetos Ativos" value={resumoProjetos.ativos} icon="ri-folder-chart-line" variant="success" />
        <KpiCard label="Em Campo" value={resumoProjetos.emCampo} icon="ri-map-pin-line" variant="info" />
        <KpiCard label="Em Revisão" value={resumoProjetos.emRevisao} icon="ri-edit-line" variant="warning" />
        <KpiCard label="Concluídos" value={resumoProjetos.concluidos} icon="ri-check-double-line" variant="info" />
        <KpiCard label="Árvores Cadastradas" value={resumoProjetos.totalArvores.toLocaleString("pt-BR")} icon="ri-plant-line" variant="success" />
        <KpiCard label="Área Inventariada" value={`${resumoProjetos.areaInventariada} ha`} icon="ri-landscape-line" variant="default" />
      </div>

      {/* Filters */}
      <ProjectFilters onFilter={setFilters} />

      {/* View Toggle + Results */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          {filtered.length} {filtered.length === 1 ? "projeto" : "projetos"} encontrado{filtered.length !== 1 ? "s" : ""}
          {Object.values(filters).some((v) => !!v) && " (com filtros aplicados)"}
        </p>
        <div className="flex items-center bg-white rounded-lg border border-background-200/70 p-0.5">
          <button
            onClick={() => setViewMode("cards")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "cards" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-grid-line mr-1"></i>Cards
          </button>
          <button
            onClick={() => setViewMode("table")}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${
              viewMode === "table" ? "bg-primary-50 text-primary-700" : "text-foreground-500 hover:text-foreground-700"
            }`}
          >
            <i className="ri-table-line mr-1"></i>Tabela
          </button>
        </div>
      </div>

      {/* Projects List */}
      {filtered.length > 0 ? (
        viewMode === "cards" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((projeto) => (
              <ProjectCard key={projeto.id} projeto={projeto} />
            ))}
          </div>
        ) : (
          <ProjectTable projetos={filtered} />
        )
      ) : (
        <EmptyState
          icon="ri-search-line"
          title="Nenhum projeto encontrado"
          description="Ajuste os filtros para ver mais resultados."
        />
      )}

      {/* Projetos Prioritários */}
      <div className="bg-white rounded-card border border-background-200/70 p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-warning-100 flex items-center justify-center">
            <i className="ri-flag-line text-warning-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-900 font-heading">Projetos Prioritários</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="p-3 rounded-lg border border-danger-200 bg-danger-50/50">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-danger-500"></span>
              <span className="text-xs font-medium text-danger-700">Atrasados</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{projetosAtrasados.length}</p>
            <p className="text-xs text-foreground-500">Prazo excedido</p>
          </div>
          <div className="p-3 rounded-lg border border-danger-200 bg-danger-50/50">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-danger-500"></span>
              <span className="text-xs font-medium text-danger-700">Árvores Críticas</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{projetosCriticos.length}</p>
            <p className="text-xs text-foreground-500">Risco crítico &gt;10</p>
          </div>
          <div className="p-3 rounded-lg border border-caution-200 bg-caution-50/50">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-caution-500"></span>
              <span className="text-xs font-medium text-caution-700">Baixa Evolução</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{projetosBaixaEvolucao.length}</p>
            <p className="text-xs text-foreground-500">Progresso &lt;40%</p>
          </div>
          <div className="p-3 rounded-lg border border-warning-200 bg-warning-50/50">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-warning-500"></span>
              <span className="text-xs font-medium text-warning-700">Relatório Pendente</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{projetosRelatorioPendente.length}</p>
            <p className="text-xs text-foreground-500">Em revisão</p>
          </div>
          <div className="p-3 rounded-lg border border-warning-200 bg-warning-50/50">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-2 h-2 rounded-full bg-warning-500"></span>
              <span className="text-xs font-medium text-warning-700">Dados Incompletos</span>
            </div>
            <p className="text-lg font-bold text-foreground-900">{projetosDadosIncompletos.length}</p>
            <p className="text-xs text-foreground-500">Revisão &gt;50</p>
          </div>
        </div>
      </div>
    </div>
  );
}